/**
 * A project's autonomy, in place (opened from its menu on Projects, or from a session drawer):
 * the approval level and expiry for sessions Orgus starts there, its own rules, its landing
 * gate and its pause. Every control applies at once; the org-wide defaults stay in Settings.
 */
import { Ban, Bot, Check, ExternalLink, Pencil, Plus, ScrollText, ShieldQuestion } from '@orgus/ui/icons';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { DELEGATE_CLASSES, DELEGATE_CLASS_DETAIL, DELEGATE_CLASS_LABEL, expiryPolicyFor } from '@orgus/protocol';
import type { Id, PolicyDecision, PolicyRule, PolicySettings, LandingReviewer } from '@orgus/protocol';
import { resolveSettings } from '@orgus/policy';
import { Button, Dialog, EmptyState, IconButton, RelativeTime, Select, SkeletonRows, Switch, Tabs, TabsContent, TabsList, TabsTrigger, Tooltip, cn, formatDate, formatInt, toUiError } from '@orgus/ui';
import { DAY_OPTIONS, PER_MINUTE_OPTIONS, changed, delegateStatus, formOf, inputOf, policyOf, revokeDelegate, saveDelegate, toggleClass, useDelegateAgents } from '../approvals/delegatesModel.ts';
import type { DelegateForm, DelegateStatus } from '../approvals/delegatesModel.ts';
import { useApprovals } from '../approvals/live.ts';
import { useProject } from '../../lib/store.ts';
import { useCan } from '../auth/useMe.ts';
import { RuleEditor } from '../approvals/PoliciesView.tsx';
import { DECISION_LABEL, describeMatch } from '../approvals/policyModel.ts';
import { saveExpiry, settingsWith, useExpirySettings, withoutProject } from '../approvals/expiryModel.ts';
import { projectConfig, useLanding } from '../landing/api.ts';
import { saveSettings, usePolicyV2 } from '../policy/live.ts';
import { REVIEWER_LABEL } from '../policy/model.ts';
import { permissionLabel } from '../sessions/labels.ts';
import { useTrust } from '../trust/useTrust.ts';
import { backToProjectAutonomy, closeProjectAutonomy, editProjectRule, setRuleEnabled, useProjectAutonomy } from './api.ts';
import { MODE_OPTIONS, expiryFact, expiryKey, expiryPresets, expiryShort, orgRuleCount, projectRules, sourceText } from './model.ts';

const INHERIT = 'inherit';
const ADMIN_ONLY = 'Only admins and the owner can change this.';

/** One setting: its name and where its value comes from on the left, the control on the right, its error under both. */
function Row({ label, note, error, inline, children }: { label: string; note?: ReactNode; error?: string | null; /** Keep the control beside the label at every width (a switch, a short value). */ inline?: boolean; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2 py-3">
      <div className={cn('flex gap-2', inline ? 'flex-row items-center gap-4' : 'flex-col sm:flex-row sm:items-center sm:gap-4')}>
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="text-ui text-fg">{label}</span>
          {note && <span className="text-caption text-fg-subtle">{note}</span>}
        </div>
        <div className={cn('flex min-w-0 shrink-0 items-center', inline ? 'max-w-1/2 justify-end' : 'sm:w-64 sm:justify-end')}>{children}</div>
      </div>
      {error && (
        <p role="alert" className="text-caption text-status-danger-ink">
          {error}
        </p>
      )}
    </div>
  );
}

/** Run one change, keep its error for the row it belongs to. */
function useApply() {
  const [busy, setBusy] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const run = async (key: string, fn: () => Promise<unknown>) => {
    setBusy(key);
    setErrors((e) => ({ ...e, [key]: '' }));
    try {
      await fn();
    } catch (e) {
      const u = toUiError(e);
      setErrors((x) => ({ ...x, [key]: [u.what, u.why, u.next].filter(Boolean).join(' ') }));
    } finally {
      setBusy(null);
    }
  };
  return { busy, errors, run };
}

const DECISION_ICON: Record<PolicyDecision, ReactNode> = { allow: <Check />, ask: <ShieldQuestion />, deny: <Ban /> };
const DECISION_INK: Record<PolicyDecision, string> = {
  allow: 'text-fg-muted [&_svg]:text-fg-subtle',
  ask: 'text-status-attention-ink [&_svg]:text-status-attention',
  deny: 'text-status-danger-ink [&_svg]:text-status-danger',
};

function ApprovalsTab({ projectId, projectName, canChange }: { projectId: Id; projectName: string; canChange: boolean }) {
  const v = usePolicyV2();
  const ex = useExpirySettings();
  const trust = useTrust();
  const canPause = useCan('operator');
  const { busy, errors, run } = useApply();
  const [warn, setWarn] = useState<string | null>(null);
  if (v.phase === 'loading' || ex.phase === 'loading') return <SkeletonRows rows={4} />;
  if (v.phase === 'missing') return <EmptyState title="This hub has no launch settings" description="Update Orgus on the hub machine." />;

  const layer: PolicySettings = v.config.projects[projectId] ?? {};
  const resolved = resolveSettings(v.config, projectId);
  const mode = resolved.find((r) => r.key === 'permissionMode');
  const bypass = resolved.find((r) => r.key === 'disableBypassPermissions');
  const orgMode = v.config.org.permissionMode ?? 'default';
  const orgBypass = v.config.org.disableBypassPermissions ? 'Blocked' : 'Allowed';

  const saveLayer = (key: string, patch: Partial<PolicySettings>, drop: keyof PolicySettings) =>
    run(key, async () => {
      const next: PolicySettings = { ...layer, ...patch };
      if (!(drop in patch)) delete next[drop];
      const conflicts = await saveSettings({ kind: 'project', projectId }, next);
      const lines = conflicts.flatMap((c) => c.violations);
      setWarn(lines.length ? `New sessions here are refused until the layers agree: ${lines.join(' ')}` : null);
    });

  const settings = ex.settings;
  const expiry = expiryFact(settings, projectId, projectName);
  const org = expiryPolicyFor(settings, null);
  const presets = expiryPresets(org);
  const current = expiry.own ? expiryKey(expiry.policy) : INHERIT;
  const expiryOptions = [
    { value: INHERIT, label: `Inherit (${expiryShort(org)})` },
    ...presets.map((p) => ({ value: p.key, label: p.label })),
    ...(expiry.own && !presets.some((p) => p.key === current) ? [{ value: current, label: expiry.value }] : []),
  ];

  const paused = trust.state?.pausedProjects.find((p) => p.projectId === projectId) ?? null;

  return (
    <div className="flex flex-col divide-y divide-border-subtle">
      <Row label="Permission mode" note={sourceText(mode?.source, projectName)} error={errors.mode}>
        <Select
          size="sm"
          aria-label="Permission mode"
          wrapperClassName="w-full"
          disabled={!canChange || busy === 'mode'}
          value={layer.permissionMode ?? INHERIT}
          onValueChange={(m) => void saveLayer('mode', m === INHERIT ? {} : { permissionMode: m }, 'permissionMode')}
          options={[{ value: INHERIT, label: `Inherit (${permissionLabel(orgMode)})` }, ...MODE_OPTIONS]}
        />
      </Row>
      <Row label="Bypass permissions" note={sourceText(bypass?.source, projectName)} error={errors.bypass}>
        <Select
          size="sm"
          aria-label="Bypass permissions"
          wrapperClassName="w-full"
          disabled={!canChange || busy === 'bypass'}
          value={layer.disableBypassPermissions === undefined ? INHERIT : layer.disableBypassPermissions ? 'blocked' : 'allowed'}
          onValueChange={(b) => void saveLayer('bypass', b === INHERIT ? {} : { disableBypassPermissions: b === 'blocked' }, 'disableBypassPermissions')}
          options={[
            { value: INHERIT, label: `Inherit (${orgBypass})` },
            { value: 'blocked', label: 'Blocked' },
            { value: 'allowed', label: 'Allowed' },
          ]}
        />
      </Row>
      {settings && (
        <Row label="When nobody answers" note={expiry.note} error={errors.expiry}>
          <Select
            size="sm"
            aria-label="When nobody answers"
            wrapperClassName="w-full"
            disabled={!canChange || busy === 'expiry'}
            value={current}
            onValueChange={(k) =>
              void run('expiry', async () => {
                if (k === INHERIT) await saveExpiry(withoutProject(settings, projectId));
                else {
                  const p = presets.find((x) => x.key === k);
                  if (p) await saveExpiry(settingsWith(settings, projectId, p.policy));
                }
              })
            }
            options={expiryOptions}
          />
        </Row>
      )}
      {trust.state && (
        <Row
          label="Pause write actions"
          inline
          note={
            paused ? (
              <>
                Paused by {paused.by} <RelativeTime ts={paused.at} />
              </>
            ) : (
              'Refuses new sessions, commands and changes here'
            )
          }
          error={errors.pause}
        >
          <Tooltip content={paused && !canChange ? 'Only admins and the owner can resume a project.' : !canPause ? 'Pausing needs the operator role or above.' : undefined}>
            <span tabIndex={(paused && !canChange) || !canPause ? 0 : -1} className="inline-flex">
              <Switch
                aria-label="Pause write actions"
                checked={!!paused}
                disabled={busy === 'pause' || (paused ? !canChange : !canPause)}
                onCheckedChange={(on) => void run('pause', () => (on ? trust.pause(projectId, 'Paused from the project') : trust.resume(projectId)))}
              />
            </span>
          </Tooltip>
        </Row>
      )}
      {warn && (
        <p role="status" className="py-3 text-caption text-status-attention-ink">
          {warn}
        </p>
      )}
    </div>
  );
}

function RulesTab({ projectId, canChange }: { projectId: Id; canChange: boolean }) {
  const v = usePolicyV2();
  const { busy, errors, run } = useApply();
  if (v.phase === 'loading') return <SkeletonRows rows={3} />;
  const rules = projectRules(v.policy?.rules ?? [], projectId);
  const hits = v.policy?.hits ?? {};
  const org = orgRuleCount(v.policy?.rules ?? []);
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <p className="min-w-0 flex-1 text-caption text-fg-subtle">
          These win over the {formatInt(org)} rules for every project.
        </p>
        <Tooltip content={canChange ? undefined : ADMIN_ONLY}>
          <span tabIndex={canChange ? -1 : 0} className="inline-flex shrink-0">
            <Button size="sm" variant="ghost" icon={<Plus />} disabled={!canChange} onClick={() => editProjectRule(null)}>
              Add rule
            </Button>
          </span>
        </Tooltip>
      </div>
      {rules.length === 0 ? (
        <EmptyState icon={<ScrollText />} title="No rules for this project" description="Sessions here follow the rules for every project." />
      ) : (
        <ul className="-mx-2 flex flex-col" aria-label="Rules for this project">
          {rules.map((r) => {
            const off = r.enabled === false;
            return (
              <li key={r.id} className="flex flex-col gap-1 rounded-control px-2 py-2 hover:bg-hover">
                <div className="flex min-w-0 items-center gap-3">
                  <span className={cn('flex size-4 shrink-0 items-center [&_svg]:size-4', DECISION_INK[r.decision], off && 'opacity-60')} aria-hidden>
                    {DECISION_ICON[r.decision]}
                  </span>
                  <span className={cn('flex min-w-0 flex-1 flex-col', off && 'opacity-60')}>
                    <span className="truncate font-mono text-caption text-fg" title={describeMatch(r.match)}>
                      {describeMatch(r.match)}
                    </span>
                    <span className="truncate text-caption text-fg-subtle num">
                      <span className={DECISION_INK[r.decision]}>{DECISION_LABEL[r.decision]}</span>
                      {off ? ', off' : `, ${formatInt(hits[r.id] ?? r.hits ?? 0)} hits in 7 days`}
                    </span>
                  </span>
                  <Switch size="sm" aria-label={`${off ? 'Turn on' : 'Turn off'} the rule ${describeMatch(r.match)}`} checked={!off} disabled={!canChange || busy === r.id} onCheckedChange={(on) => void run(r.id, () => setRuleEnabled(r, on))} />
                  <IconButton size="xs" label={`Edit the rule ${describeMatch(r.match)}`} icon={<Pencil />} disabled={!canChange} onClick={() => editProjectRule(r)} />
                </div>
                {errors[r.id] && (
                  <p role="alert" className="text-caption text-status-danger-ink">
                    {errors[r.id]}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function LandingTab({ projectId, projectName, defaultBranch, canChange }: { projectId: Id; projectName: string; defaultBranch: string; canChange: boolean }) {
  const v = usePolicyV2();
  const landing = useLanding();
  const { busy, errors, run } = useApply();
  if (v.phase === 'loading') return <SkeletonRows rows={3} />;
  const layer: PolicySettings = v.config.projects[projectId] ?? {};
  const resolved = resolveSettings(v.config, projectId);
  const reviewer = resolved.find((r) => r.key === 'reviewer');
  const checks = resolved.find((r) => r.key === 'requiredChecks');
  const checkList = Array.isArray(checks?.value) ? (checks.value as string[]) : [];
  const orgReviewer = (v.config.org.reviewer ?? 'none') as LandingReviewer;
  const cfg = landing.state ? projectConfig(landing.state, projectId, defaultBranch) : null;
  return (
    <div className="flex flex-col divide-y divide-border-subtle">
      <Row label="Reviewer" note={sourceText(reviewer?.source, projectName)} error={errors.reviewer}>
        <Select
          size="sm"
          aria-label="Reviewer"
          wrapperClassName="w-full"
          disabled={!canChange || busy === 'reviewer'}
          value={layer.reviewer ?? INHERIT}
          onValueChange={(r) =>
            void run('reviewer', async () => {
              const next: PolicySettings = { ...layer };
              if (r === INHERIT) delete next.reviewer;
              else next.reviewer = r as LandingReviewer;
              await saveSettings({ kind: 'project', projectId }, next);
            })
          }
          options={[{ value: INHERIT, label: `Inherit (${REVIEWER_LABEL[orgReviewer]})` }, ...(Object.keys(REVIEWER_LABEL) as LandingReviewer[]).map((k) => ({ value: k, label: REVIEWER_LABEL[k] }))]}
        />
      </Row>
      <Row label="Required checks" note={sourceText(checks?.source, projectName)} inline>
        <span className="truncate text-ui text-fg-muted">{checkList.length ? checkList.join(', ') : 'None'}</span>
      </Row>
      <Row inline label="Verify command" note={cfg ? (cfg.verifyCommand ? `Times out after ${formatInt(cfg.verifyTimeoutMin)} minutes` : 'Landings wait until it is set') : landing.phase === 'error' ? 'This hub has no landing queue' : 'Loading'}>
        {cfg?.verifyCommand ? (
          <span className="min-w-0 truncate font-mono text-caption text-fg-muted" title={cfg.verifyCommand}>
            {cfg.verifyCommand}
          </span>
        ) : (
          <span className="text-ui text-fg-subtle">Not set</span>
        )}
      </Row>
      <div className="flex flex-wrap gap-x-4 gap-y-1 pt-3">
        <Link to={`/settings/landing-queue?project=${encodeURIComponent(projectId)}`} className="inline-flex items-center gap-2 text-caption text-fg-muted hover:text-fg" onClick={closeProjectAutonomy}>
          Edit the verify command
          <ExternalLink className="size-3 text-fg-subtle" aria-hidden />
        </Link>
        <Link to="/settings/landing" className="inline-flex items-center gap-2 text-caption text-fg-muted hover:text-fg" onClick={closeProjectAutonomy}>
          Edit required checks and freezes
          <ExternalLink className="size-3 text-fg-subtle" aria-hidden />
        </Link>
      </div>
    </div>
  );
}

const STATUS_LINE: Record<DelegateStatus, string> = {
  on: 'answers routine requests from missions here',
  expired: 'answered here until its policy expired',
  revoked: 'answered here until the policy was revoked',
  none: 'No agent answers here. Every request waits for a person.',
};

/**
 * Delegate (ADR 0074): which agent answers a mission's routine requests here, which kinds, how
 * often and for how long. Admins change it; any operator revokes it at once.
 */
function DelegateTab({ projectId, canChange }: { projectId: Id; canChange: boolean }) {
  const { phase, approvals } = useApprovals();
  const agents = useDelegateAgents();
  const canRevoke = useCan('operator');
  const { busy, errors, run } = useApply();
  const now = Date.now();
  const policy = policyOf(approvals, projectId);
  const status = delegateStatus(policy, now);
  const [draft, setDraft] = useState<DelegateForm | null>(null);
  if (phase === 'loading' || (canChange && agents.phase === 'loading')) return <SkeletonRows rows={5} />;
  if (phase !== 'ready' || !approvals || !('delegates' in approvals)) {
    return <EmptyState icon={<Bot />} title="This hub cannot delegate approvals yet" description="Update Orgus on the hub machine." />;
  }
  const form = draft ?? formOf(policy, now, agents.agents);
  const set = (f: DelegateForm) => setDraft(f);
  const checked = inputOf(form);
  const agentOptions = [
    ...agents.agents.map((a) => ({ value: a.name, label: `agent:${a.name}`, disabled: !!a.problem })),
    ...(form.agent && !agents.agents.some((a) => a.name === form.agent) ? [{ value: form.agent, label: `agent:${form.agent}`, disabled: true }] : []),
  ];
  const chosen = agents.agents.find((a) => a.name === form.agent);
  const save = () =>
    run('save', async () => {
      if (!checked.ok) throw new Error(checked.error);
      await saveDelegate(projectId, checked.input);
      setDraft(null);
    });
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-3 pt-2">
        <Bot className={cn('size-4 shrink-0', status === 'on' ? 'text-fg-muted' : 'text-fg-subtle')} aria-hidden />
        <div className="flex min-w-0 flex-1 flex-col">
          <span className="text-ui text-fg">{policy && status !== 'none' ? `agent:${policy.agent} ${STATUS_LINE[status]}` : STATUS_LINE.none}</span>
          {policy && (
            <span className="truncate text-caption text-fg-subtle num">
              {formatInt(policy.answered ?? 0)} answered
              {policy.lastAnsweredAt ? (
                <>
                  , last <RelativeTime ts={policy.lastAnsweredAt} />
                </>
              ) : null}
              {status === 'on' ? `, until ${formatDate(policy.expiresAt, now)}` : ''}, version {formatInt(policy.version)} by {policy.updatedBy}
            </span>
          )}
        </div>
      </div>
      <div className="flex flex-col divide-y divide-border-subtle">
        <Row label="Agent" note={chosen?.problem ?? (agents.phase === 'error' ? 'Orgus could not list the agent tokens. Close and open this again.' : agentOptions.length ? 'An agent token with the approval.approve scope' : 'Create an agent token with the approval.approve scope in Settings, API tokens')}>
          <Select size="sm" aria-label="Agent" wrapperClassName="w-full" placeholder="Pick an agent" disabled={!canChange} value={form.agent || undefined} onValueChange={(agent) => set({ ...form, agent })} options={agentOptions} />
        </Row>
        <div className="flex flex-col py-3">
          <span className="pb-1 text-ui text-fg">Answers</span>
          <ul className="flex flex-col" aria-label="Kinds of request the agent answers">
            {DELEGATE_CLASSES.map((c) => (
              <li key={c} className="flex items-center gap-4 py-1">
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="text-ui text-fg-muted">{DELEGATE_CLASS_LABEL[c]}</span>
                  <span className="truncate text-caption text-fg-subtle" title={DELEGATE_CLASS_DETAIL[c]}>
                    {DELEGATE_CLASS_DETAIL[c]}
                  </span>
                </span>
                <Switch size="sm" aria-label={DELEGATE_CLASS_LABEL[c]} checked={form.classes.includes(c)} disabled={!canChange} onCheckedChange={(on) => set(toggleClass(form, c, on))} />
              </li>
            ))}
          </ul>
        </div>
        <Row label="Limits" note="Past the rate, requests wait for a person. Never past the token's own expiry">
          <div className="flex w-full gap-2">
            <Select size="sm" aria-label="Answers per minute" wrapperClassName="flex-1" disabled={!canChange} value={String(form.perMinute)} onValueChange={(v) => set({ ...form, perMinute: Number(v) })} options={PER_MINUTE_OPTIONS.map((n) => ({ value: String(n), label: `${formatInt(n)} a minute` }))} />
            <Select size="sm" aria-label="Lasts" wrapperClassName="flex-1" disabled={!canChange} value={String(form.days)} onValueChange={(v) => set({ ...form, days: Number(v) })} options={DAY_OPTIONS.map((d) => ({ value: String(d), label: d === 1 ? 'For 1 day' : `For ${formatInt(d)} days` }))} />
          </div>
        </Row>
      </div>
      <p className="text-caption text-fg-subtle">Never answered by an agent: the network, secrets, the Orgus home, inline code, hooks and agent settings, and anything outside the mission worktree.</p>
      {(errors.save || errors.revoke) && (
        <p role="alert" className="text-caption text-status-danger-ink">
          {errors.save || errors.revoke}
        </p>
      )}
      <div className="flex items-center justify-end gap-2">
        {status === 'on' && (
          <Tooltip content={canRevoke ? undefined : 'Revoking needs the operator role or above.'}>
            <span tabIndex={canRevoke ? -1 : 0} className="inline-flex">
              <Button size="sm" variant="ghost" icon={<Ban />} loading={busy === 'revoke'} disabled={!canRevoke} onClick={() => void run('revoke', async () => { await revokeDelegate(projectId); setDraft(null); })}>
                Revoke policy
              </Button>
            </span>
          </Tooltip>
        )}
        <Tooltip content={canChange ? undefined : ADMIN_ONLY}>
          <span tabIndex={canChange ? -1 : 0} className="inline-flex">
            <Button size="sm" variant="primary" loading={busy === 'save'} disabled={!canChange || !checked.ok || !changed(policy, form, now)} onClick={() => void save()}>
              {status === 'on' ? 'Save policy' : 'Turn on policy'}
            </Button>
          </span>
        </Tooltip>
      </div>
    </div>
  );
}

type Tab = 'approvals' | 'rules' | 'delegate' | 'landing';

export function ProjectAutonomyDialog({ projectId, open, onClose }: { projectId: Id | null; open: boolean; onClose: () => void }) {
  const project = useProject(projectId);
  const canChange = useCan('admin');
  const v = usePolicyV2();
  const [tab, setTab] = useState<Tab>('approvals');
  const name = project?.name ?? 'this project';
  const ruleCount = projectId ? projectRules(v.policy?.rules ?? [], projectId).length : 0;
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => !o && onClose()}
      title={`Autonomy for ${name}`}
      description={canChange ? 'For sessions Orgus starts here. Watched sessions keep their own settings.' : `For sessions Orgus starts here. ${ADMIN_ONLY}`}
      footer={
        <>
          <Link to="/settings/policies" className="mr-auto inline-flex items-center gap-2 text-caption text-fg-muted hover:text-fg" onClick={onClose}>
            Defaults for every project
            <ExternalLink className="size-3 text-fg-subtle" aria-hidden />
          </Link>
          <Button onClick={onClose}>Close</Button>
        </>
      }
    >
      {projectId && (
        <Tabs value={tab} onValueChange={(t) => setTab(t as Tab)} className="flex flex-col gap-2">
          <TabsList aria-label="Autonomy settings">
            <TabsTrigger value="approvals">Approvals</TabsTrigger>
            <TabsTrigger value="rules" count={ruleCount || undefined}>
              Rules
            </TabsTrigger>
            <TabsTrigger value="delegate">Delegate</TabsTrigger>
            <TabsTrigger value="landing">Landing</TabsTrigger>
          </TabsList>
          <TabsContent value="approvals">
            <ApprovalsTab projectId={projectId} projectName={name} canChange={canChange} />
          </TabsContent>
          <TabsContent value="rules" className="pt-2">
            <RulesTab projectId={projectId} canChange={canChange} />
          </TabsContent>
          <TabsContent value="delegate">
            <DelegateTab projectId={projectId} canChange={canChange} />
          </TabsContent>
          <TabsContent value="landing">
            <LandingTab projectId={projectId} projectName={name} defaultBranch={project?.defaultBranch ?? 'main'} canChange={canChange} />
          </TabsContent>
        </Tabs>
      )}
    </Dialog>
  );
}

/** Mounted once in the shell: the dialog, and the rule editor it steps aside for. */
export function ProjectAutonomyHost() {
  const { projectId, rule } = useProjectAutonomy();
  return (
    <>
      {/* Mounted only while open: its data hooks seed and fetch, which no page should pay for until asked. */}
      {projectId && <ProjectAutonomyDialog projectId={projectId} open={!rule} onClose={closeProjectAutonomy} />}
      {projectId && (
        <RuleEditor
          open={!!rule}
          onOpenChange={(o) => !o && backToProjectAutonomy()}
          editing={rule?.editing ?? null}
          initial={{ scope: projectId }}
        />
      )}
    </>
  );
}
